/**
 * Mixin holds common functionality (especially first initialization and destruction) of c-slider-X components.
 *
 * @mixin
 */
export default {
  mounted() {
    if (!this.createSlider) {
      throw new Error('missing method createSlider');
    }

    if (!this.$refs.stage) {
      throw new Error('missing stage reference');
    }

    /*
     * NOTE: This defers the initialization of the slider under special circumstances.
     * If the slides used inside the slider have a css class that is defined inside a webpack chunk which is loaded asynchronously, these elements can have a width of 0.
     * This results in the slider making wrong calculations for its number of pages and currently visible slides, consequently messing up the pagination controls.
     * In this case we wait until the elements change their width, meaning the css has been loaded and rendered.
     * Under normal circumstances the initialization happens immediately as before.
     *
     * Waiting on the children to gain their actual width could also be done more 'elegantly' using ResizeObserver.
     * But since this only happens in certain cases and only once per slider per page, it seems overkill to pull in a polyfill for ResizeObserver.
     */
    const startTime = Date.now();
    const checkSlidesReady = () => {
      const nodes = this.$refs.stage.children;
      let width = 0;

      this._initTimeout = null;

      // initialize if the slider is empty
      if (!nodes?.length) {
        this.createSlider();

        return;
      }

      for (let i = 0; i < nodes?.length; i += 1) {
        width += nodes[i].getBoundingClientRect()?.width || 0;
      }

      if (width) {
        this.createSlider();
      } else {
        // NOTE: for being defensive. if children don't have a meaningful width after 30 seconds we initialize anyway
        if (Date.now() - startTime > 30 * 1000) {
          this.createSlider();

          return;
        }
        this._initTimeout = setTimeout(checkSlidesReady, 50);
      }
    };

    checkSlidesReady();
  },
  beforeUnmount() {
    if (this._initTimeout) {
      clearTimeout(this._initTimeout);
    }

    if (this.sliderInstance) {
      this.sliderInstance.destroy();
    }
  },

  methods: {
    /**
     * Returns a list of unique scroll snap points.
     *
     * @returns {Array}
     */
    getScrollSnapList() {
      const scrollSnapList = this.sliderInstance?.scrollSnapList() || [];

      return scrollSnapList.filter((value, index, self) => {
        const tolerance = 0.0000001; // Tolerance for floating point comparison.

        return index === self.findIndex((selfValue) => Math.abs(selfValue - value) < tolerance);
      });
    },
  },
};
